// Model gating for the QA Copilot dropdowns. Everything here keys off the live
// `deprecated` flag the API puts on each option, so (un)deprecating a model is a DB
// change, not a deploy. The one hardcoded value is STANDARD_MODEL — the model new
// prompts default to — kept as a single knob so it's trivial to change.

import { isEmpty } from 'lodash'

// The model new QA Copilot prompts start on. Change name + reasoning_effort here to
// steer the default; nothing else is hardcoded.
const STANDARD_MODEL = { name: 'gpt-5.6-luna', reasoning_effort: 'medium' }

export const findStandardModel = (options) =>
  options.find(
    (option) =>
      option.name === STANDARD_MODEL.name &&
      (option.reasoning_effort || 'none') === STANDARD_MODEL.reasoning_effort
  )

// Deprecated models are server-enforced (summary-service 422s on save), so saving a
// prompt onto one is impossible — we hard-block in the UI to warn earlier.
export const modelBlocksSave = (option) => Boolean(option?.deprecated)

// True when `value` selects a deprecated model in `options` — the shared primitive behind
// every surface's save-block, so the "find the option, is it deprecated" rule lives once.
export const selectedModelBlocksSave = (options = [], value) =>
  modelBlocksSave(options.find((option) => option.value === value))

// Criterion save-block: a criterion runs a model when it writes a prompt — when it is AI, or
// manual carrying timestamp markers. Either can be saved onto a deprecated model and 422, so
// both are blocked. `manualTimestampMarkers` arrives from the form as the string 'true'.
export const criterionModelBlocksSave = ({
  criteriaType,
  manualTimestampMarkers,
  options,
  value,
}) =>
  (criteriaType === 'ai' || manualTimestampMarkers === true || manualTimestampMarkers === 'true') &&
  selectedModelBlocksSave(options, value)

// Eligibility-prompt save-block: only a prompt editor rewriting a non-empty prompt onto a
// deprecated model is blocked. `promptChanged` is what keeps the rest of the settings editable
// on a deprecated model — a save only POSTs the eligibility prompt when its content actually
// changed, so an untouched one never reaches the 422 and must not block unrelated settings.
// Shared by the eligibility form (inline notice) and its modal (Update button) so the two
// can't drift apart.
export const eligibilityModelBlocksSave = ({
  isPromptEditor,
  userPrompt,
  promptChanged,
  options,
  value,
}) =>
  isPromptEditor && !isEmpty(userPrompt) && promptChanged && selectedModelBlocksSave(options, value)

// Fallback-model save-block. The fallback model is a config field, not a prompt one, so there is no
// "only the changed ones get written" to lean on — every save carries it. The guard is therefore
// whether this save *puts* a deprecated model there: the strategy has to be on (an off one writes
// null), and the selection has to differ from the one the config already holds. That leaves a
// scorecard whose fallback model was deprecated under it saveable, exactly like its eligibility
// prompt, while a deliberate move onto a deprecated model is still blocked.
export const fallbackModelBlocksSave = ({ scoresToFallback, savedValue, options, value }) =>
  !isEmpty(scoresToFallback) && value !== savedValue && selectedModelBlocksSave(options, value)

export const MODEL_DEPRECATED_MESSAGE =
  'This model is deprecated and can no longer be saved. Switch to a supported model to save your changes.'

// New prompts can't pick a deprecated model; existing prompts keep the full list so a
// prompt already on a deprecated model still shows (and can be switched off) its selection.
export const getSelectableModelOptions = (options = [], { isNew } = {}) =>
  isNew ? options.filter((option) => !option.deprecated) : options
